import { createContext, useState, useContext } from 'react';

import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemRadio } from '@atlaskit/dropdown-menu';
import ChevronRightIcon from '@atlaskit/icon/glyph/chevron-right';
import Lozenge from '@atlaskit/lozenge';
import { ButtonItem, Section } from '@atlaskit/menu';
import { Box } from '@atlaskit/primitives';
import { ThemeColorModes } from '@atlaskit/tokens';

import * as store from '../../navigation-store';
import { AnalyticsEvent } from '../../types';
import { usePref } from '../../utils/use-pref';

import DarkModeImage from './assets/dark-mode.svg';
import LightModeImage from './assets/light-mode.svg';
import MatchBrowserImage from './assets/match-browser.svg';
import messages from './theme-selector.i18n';
import {
  ThemeImageLabelContainer,
  ThemeItemLabelContainer,
  FeedbackContainer,
} from './theme-selector.style';
import { ThemeSelectorProps, ThemeOptionType } from './theme-selector.types';
import { getBrowserTheme } from './utils';

interface ThemeSelectorInterface {
  setFeedbackModalOpen: (value: boolean) => void;
  publishUiEvent: (event: AnalyticsEvent) => void;
}

export const ThemeSelectorContext = createContext<ThemeSelectorInterface>({
  setFeedbackModalOpen: () => {},
  publishUiEvent: () => {},
});

export const themeOptions: readonly ThemeOptionType[] = [
  {
    label: <FormattedMessage {...messages.light} />,
    value: 'light',
    image: LightModeImage,
  },
  {
    label: <FormattedMessage {...messages.dark} />,
    value: 'dark',
    image: DarkModeImage,
  },
  {
    label: <FormattedMessage {...messages.matchBrowser} />,
    value: 'auto',
    image: MatchBrowserImage,
  },
] as const;

export const ThemeSelector = ({
  onThemeChange,
  testId,
  isBeta,
}: ThemeSelectorProps): JSX.Element => {
  const [, updateUserGlobalTheme] = usePref('global-theme', true);

  // Cached user pref global theme on page load
  const userGlobalTheme = store.getColorTheme() || 'light';

  const [theme, setCurrentTheme] = useState<ThemeColorModes>(userGlobalTheme);

  const { setFeedbackModalOpen, publishUiEvent } =
    useContext(ThemeSelectorContext);

  return (
    <DropdownMenu
      testId={testId}
      placement="auto-start"
      trigger={({ triggerRef, ...props }) => (
        <ButtonItem
          ref={triggerRef}
          onClick={event => event.stopPropagation()}
          iconAfter={<ChevronRightIcon label="" />}
          {...props}
        >
          <Box
            xcss={ThemeItemLabelContainer}
            onClick={() =>
              publishUiEvent({
                action: 'clicked',
                actionSubject: 'menuItem',
                actionSubjectId: 'theme',
                source: 'navigation',
              })
            }
          >
            <FormattedMessage {...messages.title} />
            {isBeta && <Lozenge appearance="new">Beta</Lozenge>}
          </Box>
        </ButtonItem>
      )}
    >
      {themeOptions.map(({ label, value, image }) => (
        <DropdownItemRadio
          testId={`${testId}--radio-${value}`}
          id={value}
          key={value}
          onClick={() => {
            publishUiEvent({
              action: 'changed',
              actionSubject: 'themePreference',
              actionSubjectId: 'theme',
              source: 'navigation',
              attributes: {
                newPreference: value,
                oldPreference: theme,
                browserTheme: getBrowserTheme(),
              },
            });

            setCurrentTheme(value);

            // Update user preferences and localStorage cache
            updateUserGlobalTheme(value);
            store.setColorTheme(value);

            onThemeChange(value);
          }}
          isSelected={theme === value}
        >
          <Box xcss={ThemeImageLabelContainer}>
            <img
              src={image}
              width={64}
              height={48}
              alt={label.props.defaultMessage}
            />
            {label}
          </Box>
        </DropdownItemRadio>
      ))}
      {isBeta && (
        <>
          <Section hasSeparator>
            <Box xcss={FeedbackContainer}>
              <FormattedMessage
                {...messages.feedbackMessage}
                values={{
                  link: (
                    <Button
                      appearance="link"
                      onClick={() => setFeedbackModalOpen(true)}
                      spacing="none"
                    >
                      <FormattedMessage {...messages.feedbackMessageLink} />
                    </Button>
                  ),
                }}
              />
            </Box>
          </Section>
        </>
      )}
    </DropdownMenu>
  );
};
